import React, { useState } from "react";
import { Box } from "@mui/system";
import moment from "moment";
import { downloadSampleExcelFile, downloadSampleExcelFromBlob } from "../../../helper/utils";
import Controls from "../../../component_new/controls/Controls";
import CustomizedRadios from "../../../components/CustomRadioGroup";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import { useDispatch } from "react-redux";
import { Typography } from "@mui/material";
import { NotificationActions } from "../../../redux/slices/componentSlice/notificationSlice";
import HistoryTable from "../Table";
import { useHistoryDataSelector } from "../Hooks.js";
import retailerMappingThunkActions from "../../../redux/slices/retailerMapping/thunk/retailerMappingThunk.js";

const defaultOptions = [
  { label: "Insert", value: "insert" },
  { label: "Delete", value: "delete" },
];

const GenericBulkUpload = ({
  apiModule,
  uploadType,
  labels = {},
  options = defaultOptions,
  HistoryTableComponent = HistoryTable,
  historySelector,
  historyThunk,
  historyType,
}) => {
  const dispatch = useDispatch();
  const [refreshTable, setRefreshTable] = useState(false);
  const [action, setAction] = useState("insert");
  
  const defaultHistorySelector = useHistoryDataSelector();

  const downloadStatusFile = (e) => {
    e.preventDefault();
    const formData = new FormData();
    if (e.target && e.target.files[0]) {
      formData.append("file", e.target.files[0]);
    }
    apiModule
      .upload({ formData, action, uploadType })
      .then((res) => {
        dispatch(
          NotificationActions.addNotification({
            open: true,
            message: labels.uploadSuccess || "Successfully Uploaded",
            timeOut: 3000,
          })
        );
        setTimeout(() => {
          setRefreshTable((prev) => !prev);
        }, 2000);
      })
      .catch((error) => {
        dispatch(
          NotificationActions.addNotification({
            open: false,
            message: labels.uploadFail || "Failed to upload file!",
            timeOut: 3000,
          })
        );
      });
    e.target.value = "";
  };
  const handleChange = (value) => {
    setAction(value);
  };
  const handleDownloadResponse = (res, filePrefix = "bulk_data_", successMessage = "Successfully Downloaded Data") => {
    if (res?.data) {
      if (typeof res.data === 'string') {
        try {
          // Try to parse as JSON first
          const parsedData = JSON.parse(res.data);
          if (parsedData && typeof parsedData === 'object' && parsedData.data) {
            downloadSampleExcelFile(
              parsedData.data,
              `${filePrefix}${moment().format("DMMY_hhmmss")}`
            );
          } else {
            // Direct base64 data in JSON format
            downloadSampleExcelFile(
              parsedData,
              `${filePrefix}${moment().format("DMMY_hhmmss")}`
            );
          }
        } catch (e) {
          // If not JSON, treat as direct base64 string
          downloadSampleExcelFile(
            res.data,
            `${filePrefix}${moment().format("DMMY_hhmmss")}`
          );
        }
      } else if (res.data instanceof Blob) {
        const extension = '.xlsx';
        downloadSampleExcelFromBlob(res.data, `${filePrefix}
          ${moment().format("DMMY_hhmmss")}${extension}`);
      }
    }
    dispatch(
      NotificationActions.addNotification({
        open: true,
        message: successMessage,
        timeOut: 3000,
      })
    );
  };

  const handleDownload = () => {
    apiModule
      .download({ uploadType })
      .then((res) => {
        handleDownloadResponse(res, labels.downloadFilePrefix || "bulk_data_", labels.downloadSuccess 
          || "Successfully Downloaded Data");
      })
      .catch((error) => {
        dispatch(
          NotificationActions.addNotification({
            open: false,
            message: labels.downloadFail || "Download failed!",
            timeOut: 3000,
          })
        );
      });
  };

  const handleTemplateDownload = () => {
    if(action === "insert"){
      apiModule.downloadInsertTemplate().then((res) => {
        downloadSampleExcelFromBlob(res.data, labels.insertTemplateName || 'insert_bulk_data_', labels.downloadSuccess
          || "Successfully Downloaded Insert Template");
      });
    }else if(action === "delete"){
      apiModule.downloadDeleteTemplate().then((res) => {
        downloadSampleExcelFromBlob(res.data, labels.deleteTemplateName || "delete_bulk_data_", labels.downloadSuccess
          || "Successfully Downloaded Delete Template");
      });
    }
  };

  const handleDownloadDataByRequestId = (requestId) => {
    apiModule.downloadByRequestId(requestId)
    .then((res) => {
      const extension = '.xlsx';
      const filePrefix = `${labels.downloadFilePrefix}${requestId}`;
      downloadSampleExcelFromBlob(res.data, `${filePrefix}
        ${moment().format("DMMY_hhmmss")}${extension}`);
        dispatch(
          NotificationActions.addNotification({
            open: true,
            message: labels.downloadSuccess,
            timeOut: 3000,
          })
        );
    })
    .catch((error) => {
      console.error('Download failed:', error);
      dispatch(
        NotificationActions.addNotification({
          open: true,
          message: "Download failed!",
          timeOut: 3000,
        })
      );
    })
  }
 

  return (
    <Box sx={{ padding: "20px" }}>
      <div>
        <div
          style={{
            display: "flex",
            border: "1px solid #bcbcbc",
            background: "#e7e7e7",
            padding: "8px",
            width: "100%",
            borderRadius: "4px",
          }}
        >
          <CustomizedRadios
            row
            options={options}
            handleChange={handleChange}
            value={action}
          />
          <Controls.Button
            text={labels.uploadButton || "Upload File"}
            component="label"
            style={{
              lineHeight: 1,
              borderRadius: "8px",
              textTransform: "none",
              padding: "5px 10px",
              minWidth: "100px",
              maxWidth: "250px",
            }}
            endIcon={<UploadIcon />}
          >
            <input
              type="file"
              accept=".xlsx"
              hidden
              onChange={(e) => downloadStatusFile(e)}
              onClick={(e) => {
                e.target.value = null;
              }}
            />
          </Controls.Button>
        </div>
        <br />
        <div style={{display: "flex", gap: "10px"}}>
          <Controls.Button
            text={labels.downloadButton || "Download Data"}
            component="label"
            style={{
              lineHeight: 1,
              borderRadius: "8px",
              textTransform: "none",
              padding: "5px 10px",
              minWidth: "100px",
              maxWidth: "150px",
            }}
            onClick={() => handleDownload()}
            endIcon={<DownloadIcon />}
          ></Controls.Button>
          <Controls.Button
            text={labels.templateButton || "Download Template"}
            component="label"
            style={{
              lineHeight: 1,
              borderRadius: "8px",
              textTransform: "none",
              padding: "5px 10px",
              minWidth: "100px",
              maxWidth: "150px",
            }}
            onClick={() => {
              handleTemplateDownload();
            }}
            endIcon={<DownloadIcon />}
          ></Controls.Button>
        </div>
      </div>
      <br />
      <div>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "16px",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontWeight: "bold" }} variant="h7" gutterBottom>
            {labels.statusHistory || "Status History"}
          </Typography>
          <Controls.Button
            text={labels.refreshButton || "Refresh"}
            component="label"
            style={{
              lineHeight: 1,
              borderRadius: "8px",
              textTransform: "none",
              padding: "5px 10px",
              minWidth: "100px",
              height: "36px",
            }}
            onClick={() => {
              setRefreshTable((prev) => !prev);
            }}
          ></Controls.Button>
        </Box>
        <HistoryTableComponent
          refresh={refreshTable}
          selector={historySelector || defaultHistorySelector}
          thunk={historyThunk || retailerMappingThunkActions.getRetailerMappingHistory}
          type={historyType || "retailerMapping"}
          onDownload={(requestId)=>handleDownloadDataByRequestId(requestId)}
        />
      </div>
    </Box>
  );
};

export default GenericBulkUpload; 